import {lazy, Suspense} from 'react'
import {ThemeProvider} from '@primer/react-brand'

import {FootnotesProvider} from '../../../components/contentful/ContentfulFootnotes/FootnotesContext'
import type {FlexPage} from '../../../schemas/contentful/contentTypes/flexTemplate'
import {PersonalizationProvider, type PersonalizationContextValue} from '../../../contexts/PersonalizationContext'
import {useDomReady} from '../../../lib/utils/platform'
import {useNinetailedConfig} from '../../../contexts/NinetailedConfigContext'

import {FlexTemplateContent} from './FlexTemplateContent'

// Lazy load Ninetailed wrapper to prevent SSR bundling
const NinetailedWrapper = lazy(() => import('../../../components/ninetailed/NinetailedWrapper'))

export type FlexTemplateProps = {
  page: FlexPage
  userLoggedIn?: boolean
  hasNtExperiences?: boolean
}

export function FlexTemplate({page, userLoggedIn, hasNtExperiences}: FlexTemplateProps) {
  const isDomReady = useDomReady()
  const ninetailedConfig = useNinetailedConfig()

  const personalizationValue: PersonalizationContextValue = {
    userState: {
      isLoggedIn: userLoggedIn,
    },
  }

  const templateContent = (
    <ThemeProvider
      colorMode={page.fields.settings?.fields.colorMode ?? 'light'}
      style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
    >
      <PersonalizationProvider value={personalizationValue}>
        <FootnotesProvider>
          <FlexTemplateContent page={page} />
        </FootnotesProvider>
      </PersonalizationProvider>
    </ThemeProvider>
  )

  // Only wrap with Ninetailed when experiences exist and DOM is ready
  if (hasNtExperiences && ninetailedConfig && isDomReady) {
    return (
      <Suspense fallback={null}>
        <NinetailedWrapper
          clientId={ninetailedConfig.clientId}
          environment={ninetailedConfig.environment}
          locale={ninetailedConfig.locale}
        >
          {templateContent}
        </NinetailedWrapper>
      </Suspense>
    )
  }

  return templateContent
}
